import { create } from 'zustand';
import { Agent } from '@/types';
import { agentsService } from '@/services/agents.service';

interface AgentState {
  agents: Agent[];
  selectedAgent: Agent | null;
  isLoading: boolean;
  error: string | null;

  setAgents: (agents: Agent[]) => void;
  setSelectedAgent: (agent: Agent | null) => void;
  fetchAgents: () => Promise<void>;
  fetchAgent: (id: string) => Promise<void>;
  createAgent: (data: any) => Promise<Agent>;
  updateAgent: (id: string, data: Partial<Agent>) => Promise<void>;
  connectWorkflow: (id: string, data: { workflow_id: string; name?: string }) => Promise<void>;
  disconnectWorkflow: (id: string) => Promise<void>;
  updateWorkflowConfig: (id: string, data: { credential_bindings: Record<string, { integration_id: string }> }) => Promise<void>;
  updateAgentStatus: (id: string, status: string) => Promise<void>;
  deleteAgent: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useAgentStore = create<AgentState>((set) => ({
  agents: [],
  selectedAgent: null,
  isLoading: false,
  error: null,

  setAgents: (agents) => set({ agents }),

  setSelectedAgent: (agent) => set({ selectedAgent: agent }),

  fetchAgents: async () => {
    set({ isLoading: true, error: null });
    try {
      const agents = await agentsService.getAgents();
      set({ agents, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch agents';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  fetchAgent: async (id: string) => {
    const state = useAgentStore.getState();
    // Only show loading spinner if no agent is loaded yet (initial load)
    if (!state.selectedAgent || state.selectedAgent.id !== id) {
      set({ isLoading: true, error: null });
    }
    try {
      const agent = await agentsService.getAgent(id);
      set({ selectedAgent: agent, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch agent';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  createAgent: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const agent = await agentsService.createAgent(data);
      set((state) => ({
        agents: [...state.agents, agent],
        isLoading: false,
      }));
      return agent;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create agent';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  updateAgent: async (id: string, data: Partial<Agent>) => {
    set({ error: null });
    try {
      const updatedAgent = await agentsService.updateAgent(id, data);
      set((state) => ({
        agents: state.agents.map((agent) => (agent.id === id ? updatedAgent : agent)),
        selectedAgent: state.selectedAgent?.id === id ? updatedAgent : state.selectedAgent,
        isLoading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update agent';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  connectWorkflow: async (id: string, data: { workflow_id: string; name?: string }) => {
    set({ error: null });
    try {
      const updatedAgent = await agentsService.connectWorkflow(id, data);
      set((state) => ({
        agents: state.agents.map((agent) => (agent.id === id ? updatedAgent : agent)),
        selectedAgent: state.selectedAgent?.id === id ? updatedAgent : state.selectedAgent,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect workflow';
      set({ error: errorMessage });
      throw error;
    }
  },

  disconnectWorkflow: async (id: string) => {
    set({ error: null });
    try {
      const updatedAgent = await agentsService.disconnectWorkflow(id);
      set((state) => ({
        agents: state.agents.map((agent) => (agent.id === id ? updatedAgent : agent)),
        selectedAgent: state.selectedAgent?.id === id ? updatedAgent : state.selectedAgent,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to disconnect workflow';
      set({ error: errorMessage });
      throw error;
    }
  },

  updateWorkflowConfig: async (id: string, data: { credential_bindings: Record<string, { integration_id: string }> }) => {
    set({ error: null });
    try {
      const updatedAgent = await agentsService.updateWorkflowConfig(id, data);
      set((state) => ({
        agents: state.agents.map((agent) => (agent.id === id ? updatedAgent : agent)),
        selectedAgent: state.selectedAgent?.id === id ? updatedAgent : state.selectedAgent,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update workflow config';
      set({ error: errorMessage });
      throw error;
    }
  },

  updateAgentStatus: async (id: string, status: string) => {
    set({ error: null });
    try {
      const updatedAgent = await agentsService.updateAgentStatus(id, status);
      set((state) => ({
        agents: state.agents.map((agent) => (agent.id === id ? updatedAgent : agent)),
        selectedAgent: state.selectedAgent?.id === id ? updatedAgent : state.selectedAgent,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update agent status';
      set({ error: errorMessage });
      throw error;
    }
  },

  deleteAgent: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await agentsService.deleteAgent(id);
      set((state) => ({
        agents: state.agents.filter((agent) => agent.id !== id),
        selectedAgent: state.selectedAgent?.id === id ? null : state.selectedAgent,
        isLoading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete agent';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
